import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { registerSW } from 'virtual:pwa-register';
import App from './App';
import { isDesktop, reloadWhenIdle } from './platform';
import './styles.css';
import './shell.css';

// Keep the deployed app current without the user ever needing a hard
// refresh. The browser's own service-worker update check runs only once
// every ~24h, which is far slower than how often this project ships, so the
// registration below polls every 5 minutes instead. `registerType:
// 'autoUpdate'` (vite.config.ts) means a new service worker activates and
// takes control (`controllerchange`) the moment it's ready; reloadWhenIdle
// then reloads immediately, unless a drawer is open or a form (e.g.
// Settings) has unsaved edits, in which case it waits for that to clear
// first rather than interrupting someone mid-entry.
if (!isDesktop && 'serviceWorker' in navigator) {
  registerSW({
    immediate: true,
    onNeedRefresh() {
      reloadWhenIdle();
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      // Fast check for updates every 20 seconds
      setInterval(() => { void registration.update(); }, 20 * 1000);

      // Instantly check for updates when returning to tab or focusing window
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          void registration.update();
        }
      });
      window.addEventListener('focus', () => {
        void registration.update();
      });
    },
  });
  navigator.serviceWorker.addEventListener('controllerchange', reloadWhenIdle);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      retry: 1,
      refetchOnWindowFocus: 'always',
      refetchOnMount: 'always',
      refetchOnReconnect: 'always',
    },
  },
});

// Automatically open date picker dropdown when clicking anywhere on a date input
document.addEventListener('click', (e) => {
  const target = (e.target as HTMLElement)?.closest('input[type="date"]') as HTMLInputElement | null;
  if (target && !target.disabled && !target.readOnly && typeof target.showPicker === 'function') {
    try {
      target.showPicker();
    } catch {
      // Ignore if picker cannot be opened
    }
  }
});

// Prevent trackpad swipe / mouse wheel from incrementing or decrementing number inputs
window.addEventListener(
  'wheel',
  (e) => {
    const target = (e.target as HTMLElement)?.closest('input[type="number"]') as HTMLInputElement | null;
    const active = document.activeElement as HTMLInputElement | null;
    if (target || (active && active.tagName === 'INPUT' && active.type === 'number')) {
      if (target) target.blur();
      if (active && active.type === 'number') active.blur();
      e.preventDefault();
    }
  },
  { passive: false }
);

// Sync data-empty attribute on date inputs so mobile placeholder renders reliably
function syncDateEmptyAttrs() {
  document.querySelectorAll('input[type="date"]').forEach((el) => {
    const input = el as HTMLInputElement;
    const hasVal = Boolean(input.value && input.value.trim() !== '');
    if (!hasVal) {
      if (input.getAttribute('data-empty') !== 'true') input.setAttribute('data-empty', 'true');
    } else {
      if (input.hasAttribute('data-empty')) input.removeAttribute('data-empty');
    }
  });
}

['input', 'change', 'blur', 'focus'].forEach((evt) => {
  document.addEventListener(evt, (e) => {
    const target = (e.target as HTMLElement)?.closest('input[type="date"]') as HTMLInputElement | null;
    if (target) {
      const hasVal = Boolean(target.value && target.value.trim() !== '');
      if (!hasVal) {
        target.setAttribute('data-empty', 'true');
      } else {
        target.removeAttribute('data-empty');
      }
    }
  }, true);
});

const dateEmptyObserver = new MutationObserver(syncDateEmptyAttrs);
dateEmptyObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['value'],
});
syncDateEmptyAttrs();
setInterval(syncDateEmptyAttrs, 300);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

